import { useState } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Buildings, GlobeHemisphereEast, UsersThree, UploadSimple, RocketLaunch } from '@phosphor-icons/react';

const STEPS = [
  { key: 'org', label: 'Organisation', icon: Buildings },
  { key: 'school', label: 'Country & School', icon: GlobeHemisphereEast },
  { key: 'staff', label: 'Staff roles', icon: UsersThree },
  { key: 'import', label: 'Import students', icon: UploadSimple },
  { key: 'golive', label: 'Go-live', icon: RocketLaunch },
];

const ImportHistory = () => {
  const { data: hist } = useQuery({ queryKey: ['import-history'], queryFn: () => api.get('/imports/history').then((r) => r.data) });
  if (!hist?.length) return null;
  return (
    <div className="mt-5 pt-4 border-t border-slate-100" data-testid="import-history">
      <h3 className="font-display font-semibold mb-2">Import history</h3>
      <div className="divide-y divide-slate-50 border border-slate-100 rounded-md">
        {hist.slice(0, 10).map((h) => (
          <div key={h.id} className="flex items-center gap-2 px-3 py-1.5 text-sm flex-wrap" data-testid={`import-log-${h.id}`}>
            <span className="font-mono-label text-[9px] uppercase bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{h.kind}</span>
            <span className="text-green-700">{h.success} ok</span>
            {h.failed > 0 && <span className="text-rose-600">{h.failed} failed</span>}
            <span className="ms-auto text-xs text-slate-400">{h.by} · {(h.at || '').slice(0, 16).replace('T', ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Onboarding() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState({ name: '' });
  const [orgId, setOrgId] = useState(null);
  const [school, setSchool] = useState({ name: '', country: 'IN' });
  const [schoolId, setSchoolId] = useState(null);
  const [staff, setStaff] = useState({ email: '', name: '', role: 'teacher' });
  const [staffAdded, setStaffAdded] = useState([]);
  const [staffFile, setStaffFile] = useState(null);
  const [staffBusy, setStaffBusy] = useState(false);
  const [csv, setCsv] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [gFile, setGFile] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const [gResult, setGResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const { data: checklist, refetch: refetchChecklist } = useQuery({
    queryKey: ['checklist'], queryFn: () => api.get('/admin/checklist').then((r) => r.data), enabled: step === 4,
  });

  const targetTenant = schoolId || user?.tenant_id;
  const next = () => setStep((s) => Math.min(s + 1, 4));

  const orgMut = useMutation({
    mutationFn: () => api.post('/tenants', { name: org.name, type: 'org', country: school.country }),
    onSuccess: (r) => { setOrgId(r.data.id); toast.success('Organisation created'); next(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const schoolMut = useMutation({
    mutationFn: () => api.post('/tenants', { name: school.name, type: 'school', parent_id: orgId, country: school.country }),
    onSuccess: (r) => { setSchoolId(r.data.id); toast.success(`School created with ${school.country} country pack`); next(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const staffMut = useMutation({
    mutationFn: () => api.post('/users', { ...staff, tenant_id: targetTenant }),
    onSuccess: () => { setStaffAdded((a) => [...a, { ...staff }]); toast.success(`${staff.role} added (temp password Welcome@123)`); setStaff({ email: '', name: '', role: staff.role }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const parseRows = () => csv.trim().split('\n').filter(Boolean)
    .filter((line, i) => !(i === 0 && /name/i.test(line.split(',')[0]) && !/\d/.test(line)))
    .map((line) => {
      const [name, grade, section, dob] = line.split(',').map((x) => (x || '').trim());
      return { name, grade, section: section || 'A', dob: dob || '' };
    });
  const readFile = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = rej;
    r.readAsText(file);
  });
  const onStudentFile = async (file) => {
    if (!file) return;
    setCsvFile(file); setPreview(null);
    setCsv(await readFile(file));
  };
  const dlTemplate = (name, content) => {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };
  const STAFF_ROLES = ['school_admin', 'principal', 'registrar', 'admissions_officer', 'teacher', 'finance_admin'];
  const onStaffFile = async (file) => {
    if (!file) return;
    setStaffFile(file); setStaffBusy(true);
    try {
      const rows = (await readFile(file)).trim().split('\n').filter(Boolean)
        .filter((line, i) => !(i === 0 && /name|email/i.test(line) && !line.includes('@')))
        .map((line) => {
          const [name, email, role] = line.split(',').map((x) => (x || '').trim());
          return { name, email: email.toLowerCase(), role: STAFF_ROLES.includes(role) ? role : 'teacher' };
        }).filter((r) => r.name && r.email.includes('@'));
      if (!rows.length) { toast.error('No valid rows found - expected: name, email, role'); return; }
      let ok = 0; const fails = [];
      for (const r of rows) {
        try { await api.post('/users', { ...r, tenant_id: targetTenant }); setStaffAdded((a) => [...a, r]); ok += 1; }
        catch (e) { fails.push(`${r.email}: ${errMsg(e)}`); }
      }
      toast[fails.length ? 'warning' : 'success'](`${ok} staff imported${fails.length ? `, ${fails.length} failed (${fails[0]})` : ''} - temp password Welcome@123`);
      api.post('/imports/log', { kind: 'faculty', total: rows.length, success: ok, failed: fails.length }).then(() => qc.invalidateQueries({ queryKey: ['import-history'] }));
    } finally { setStaffBusy(false); }
  };
  const onGuardianFile = async (file) => {
    if (!file) return;
    setGFile(file); setGBusy(true); setGResult(null);
    try {
      const rows = (await readFile(file)).trim().split('\n').filter(Boolean)
        .filter((line, i) => !(i === 0 && /admission|email/i.test(line) && !line.includes('@')))
        .map((line) => {
          const [admission_no, guardian_name, guardian_email, relation, phone] = line.split(',').map((x) => (x || '').trim());
          return { admission_no, guardian_name, guardian_email, relation: relation || 'parent', phone: phone || null };
        }).filter((r) => r.admission_no && r.guardian_email);
      if (!rows.length) { toast.error('No valid rows found - expected: admission_no, guardian name, email, relation, phone'); return; }
      const { data } = await api.post('/guardians/import', { rows });
      setGResult(data);
      toast[data.error_count ? 'warning' : 'success'](`${data.linked} guardian(s) linked, ${data.created_users} account(s) created${data.error_count ? `, ${data.error_count} row(s) failed` : ''}`);
      api.post('/imports/log', { kind: 'guardians', total: rows.length, success: data.linked, failed: data.error_count }).then(() => qc.invalidateQueries({ queryKey: ['import-history'] }));
    } catch (e) { toast.error(errMsg(e)); } finally { setGBusy(false); }
  };
  const previewMut = useMutation({
    mutationFn: () => api.post('/admin/import/students/preview', { tenant_id: targetTenant, rows: parseRows() }),
    onSuccess: (r) => setPreview(r.data), onError: (e) => toast.error(errMsg(e)),
  });
  const commitMut = useMutation({
    mutationFn: () => api.post('/admin/import/students/commit', { tenant_id: targetTenant, rows: parseRows() }),
    onSuccess: (r) => {
      toast.success(`Imported ${r.data.imported} students (${r.data.skipped} dupes skipped)`);
      api.post('/imports/log', { kind: 'students', total: parseRows().length, success: r.data.imported, failed: r.data.skipped }).then(() => qc.invalidateQueries({ queryKey: ['import-history'] }));
      next(); qc.invalidateQueries({ queryKey: ['checklist'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-page">
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Pilot readiness</p>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">Onboarding wizard</h1>

      <div className="flex items-center gap-1 mb-8">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)} data-testid={`onboarding-step-${s.key}`}
            className={`flex-1 flex flex-col items-center gap-1 py-3 border-b-2 transition-colors ${i === step ? 'border-slate-900 text-slate-900' : i < step ? 'border-green-600 text-green-700' : 'border-slate-200 text-slate-400'}`}>
            <s.icon size={18} weight="duotone" />
            <span className="text-[11px] font-medium hidden sm:block">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 stagger-in">
        {step === 0 && (
          <>
            <h2 className="font-display text-xl font-semibold mb-1">Create your organisation</h2>
            <p className="text-sm text-slate-500 mb-4">The root tenant. Schools live under it with subtree-isolated data.</p>
            <Input data-testid="onboarding-org-name" placeholder="e.g. Green Valley Education Trust" value={org.name} onChange={(e) => setOrg({ name: e.target.value })} className="mb-3" />
            <div className="flex gap-2">
              <Button data-testid="onboarding-org-create" disabled={!org.name || orgMut.isPending} onClick={() => orgMut.mutate()} className="bg-slate-900 hover:bg-slate-800">Create organisation</Button>
              <Button variant="outline" onClick={next} data-testid="onboarding-org-skip">Skip (already exists)</Button>
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h2 className="font-display text-xl font-semibold mb-1">Country pack & school</h2>
            <p className="text-sm text-slate-500 mb-4">The country pack sets grading, consent regime (DPDP/FERPA) and comms rules - as data, not code.</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
            {['IN', 'US', 'AE', 'GB', 'SG', 'EU'].map((c) => (
                <button key={c} data-testid={`onboarding-country-${c}`} onClick={() => setSchool({ ...school, country: c })}
                  className={`border rounded-md px-4 py-3 text-sm font-medium flex-1 transition-colors ${school.country === c ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>
                  {{ IN: '🇮🇳 India (full pack - DPDP, A1–E grading, INR, DLT-ready)',
                     US: '🇺🇸 US (FERPA/COPPA, GPA grading, USD)',
                     AE: '🇦🇪 UAE (PDPL, MOE grading, AED)',
                     GB: '🇬🇧 UK (UK-GDPR, GCSE 9–1, GBP)',
                     SG: '🇸🇬 Singapore (PDPA, A1–F9, SGD)',
                     EU: '🇪🇺 EU (GDPR, ECTS, EUR)' }[c]}
                </button>
              ))}
            </div>
            <Input data-testid="onboarding-school-name" placeholder="School name" value={school.name} onChange={(e) => setSchool({ ...school, name: e.target.value })} className="mb-3" />
            <div className="flex gap-2">
              <Button data-testid="onboarding-school-create" disabled={!school.name || schoolMut.isPending} onClick={() => schoolMut.mutate()} className="bg-slate-900 hover:bg-slate-800">Create school</Button>
              <Button variant="outline" onClick={next} data-testid="onboarding-school-skip">Skip</Button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="font-display text-xl font-semibold mb-1">Add staff</h2>
            <p className="text-sm text-slate-500 mb-4">Upload a faculty CSV: <code className="font-mono-label text-xs">name, email, role</code> (roles: {STAFF_ROLES.join(', ')}). Temp password: Welcome@123</p>
            <label data-testid="onboarding-staff-upload" className="block border-2 border-dashed border-slate-200 rounded-md p-5 text-center cursor-pointer hover:border-orange-400 transition-colors mb-3">
              <input type="file" accept=".csv,text/csv" className="hidden" data-testid="onboarding-staff-file-input"
                onChange={(e) => onStaffFile(e.target.files?.[0])} />
              <p className="text-sm font-medium">{staffBusy ? 'Importing faculty...' : staffFile ? `${staffFile.name} - click to replace` : 'Click to upload faculty CSV'}</p>
              <p className="text-xs text-slate-400 mt-1">Example row: Sunita Verma, sunita@school.in, teacher</p>
            </label>
            <button data-testid="onboarding-staff-template" onClick={() => dlTemplate('faculty-template.csv', 'name,email,role\nSunita Verma,sunita@school.in,teacher\nRakesh Nair,rakesh@school.in,registrar\n')}
              className="text-xs text-orange-700 underline mb-3 block">Download sample faculty CSV</button>
            <details className="mb-3">
              <summary className="text-xs text-slate-500 cursor-pointer" data-testid="onboarding-staff-manual-toggle">Or add one person manually</summary>
              <div className="space-y-3 mt-3">
                <Input data-testid="onboarding-staff-name" placeholder="Full name" value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} />
                <Input data-testid="onboarding-staff-email" placeholder="Email" value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} />
                <select data-testid="onboarding-staff-role" value={staff.role} onChange={(e) => setStaff({ ...staff, role: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  {STAFF_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                </select>
                <Button data-testid="onboarding-staff-add" disabled={!staff.email || !staff.name || staffMut.isPending} onClick={() => staffMut.mutate()} className="bg-slate-900 hover:bg-slate-800">Add staff member</Button>
              </div>
            </details>
            <Button variant="outline" onClick={next} data-testid="onboarding-staff-next">Continue</Button>
            {staffAdded.length > 0 && <p className="text-sm text-green-700 mt-3" data-testid="onboarding-staff-added">✓ Added {staffAdded.length}: {staffAdded.map((s) => `${s.name} (${s.role})`).join(', ')}</p>}
          </>
        )}
        {step === 3 && (
          <>
            <h2 className="font-display text-xl font-semibold mb-1">Import students (CSV)</h2>
            <p className="text-sm text-slate-500 mb-4">Upload a CSV with columns: <code className="font-mono-label text-xs">name, grade, section, dob</code>. Validate - dedupe - preview - commit (rollback supported).</p>
            <label data-testid="onboarding-csv-upload" className="block border-2 border-dashed border-slate-200 rounded-md p-5 text-center cursor-pointer hover:border-orange-400 transition-colors">
              <input type="file" accept=".csv,text/csv" className="hidden" data-testid="onboarding-csv-file-input"
                onChange={(e) => onStudentFile(e.target.files?.[0])} />
              <p className="text-sm font-medium">{csvFile ? `${csvFile.name} - ${parseRows().length} row(s) - click to replace` : 'Click to upload student CSV'}</p>
              <p className="text-xs text-slate-400 mt-1">Example row: Ravi Kumar, 5, A, 2015-04-12</p>
            </label>
            <button data-testid="onboarding-student-template" onClick={() => dlTemplate('students-template.csv', 'name,grade,section,dob\nRavi Kumar,5,A,2015-04-12\nSneha Pillai,6,A,2014-08-03\n')}
              className="text-xs text-orange-700 underline mt-1 block">Download sample student CSV</button>
            {preview && (
              <p className="text-sm mt-2" data-testid="onboarding-preview-result">
                <span className="text-green-700">{preview.valid.length} valid</span> · <span className="text-amber-600">{preview.duplicates.length} dupes</span> · <span className="text-rose-600">{preview.errors.length} errors</span>
              </p>
            )}
            <div className="flex gap-2 mt-3">
              <Button variant="outline" data-testid="onboarding-preview-button" disabled={!csv.trim()} onClick={() => previewMut.mutate()}>Preview</Button>
              <Button data-testid="onboarding-commit-button" disabled={!preview?.valid?.length} onClick={() => commitMut.mutate()} className="bg-slate-900 hover:bg-slate-800">Commit import</Button>
              <Button variant="ghost" onClick={next} data-testid="onboarding-import-skip">Skip</Button>
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <h3 className="font-display font-semibold mb-1">Link guardians (CSV)</h3>
              <p className="text-sm text-slate-500 mb-3">Columns: <code className="font-mono-label text-xs">admission_no, guardian name, email, relation, phone</code>. Creates guardian accounts (temp password Welcome@123) and links them to students.</p>
              <label data-testid="onboarding-guardian-upload" className="block border-2 border-dashed border-slate-200 rounded-md p-5 text-center cursor-pointer hover:border-orange-400 transition-colors">
                <input type="file" accept=".csv,text/csv" className="hidden" data-testid="onboarding-guardian-file-input"
                  onChange={(e) => onGuardianFile(e.target.files?.[0])} />
                <p className="text-sm font-medium">{gBusy ? 'Linking guardians...' : gFile ? `${gFile.name} - click to replace` : 'Click to upload guardian CSV'}</p>
                <p className="text-xs text-slate-400 mt-1">Example row: ADM1001, Meena Kumar, meena@example.com, mother, 9876543210</p>
              </label>
              <button data-testid="onboarding-guardian-template" onClick={() => dlTemplate('guardians-template.csv', 'admission_no,guardian_name,guardian_email,relation,phone\nADM1001,Meena Kumar,meena@example.com,mother,9876543210\nADM1002,Suresh Pillai,suresh@example.com,father,\n')}
                className="text-xs text-orange-700 underline mt-1 block">Download sample guardian CSV</button>
              {gResult && (
                <p className="text-sm mt-2" data-testid="onboarding-guardian-result">
                  <span className="text-green-700">{gResult.linked} linked · {gResult.created_users} account(s) created</span>
                  {gResult.error_count > 0 && <span className="text-rose-600"> · {gResult.error_count} failed: {gResult.errors[0]}</span>}
                </p>
              )}
            </div>
            <ImportHistory />
          </>
        )}
        {step === 4 && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h2 className="font-display text-xl font-semibold">Go-live checklist</h2>
              <span className={`font-mono-label text-[10px] uppercase px-3 py-1 rounded ${checklist?.production_ready ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-700'}`} data-testid="onboarding-golive-status">
                {checklist?.production_ready ? 'Ready for production' : 'Promotion blocked'}
              </span>
            </div>
            {(checklist?.checks || []).map((c) => (
              <div key={c.key} className="flex items-center gap-3 py-2 border-t border-slate-100 text-sm">
                <span className={`w-5 h-5 rounded flex items-center justify-center text-xs ${c.done ? 'bg-green-700 text-white' : 'bg-slate-200 text-slate-500'}`}>{c.done ? '✓' : '·'}</span>
                {c.label}
                {c.key === 'admin_mfa' && !c.done && <button className="text-xs text-blue-600 hover:underline ml-auto" onClick={() => nav('/profile')} data-testid="onboarding-mfa-link">Enable MFA -</button>}
              </div>
            ))}
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => refetchChecklist()} data-testid="onboarding-recheck">Re-check</Button>
              <Button onClick={() => nav('/')} className="bg-slate-900 hover:bg-slate-800" data-testid="onboarding-finish">Go to dashboard</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
